import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './DataTableDIR.module.scss';
import { DataGridDIRFromDIRRow, IDirData } from '../../../../utils/GlobalTypes';
import {
  DataGrid,
  GridActionsCellItem,
  GridValueFormatterParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import DataTablePMDSkeleton from './DataTableDIRSkeleton';
import { useAppDispatch, useAppSelector } from '../../../../services/store/hooks';
import {
  setSelectedDirectionsIDs,
  setHiddenDirectionsIDs,
  setReversedDirectionsIDs,
} from '../../../../services/reducers/dirPage';
import { getDataTableBaseStyle } from '../styleConstants';
import PMDInputDataTableToolbar from '../../../Common/DataTable/Toolbar/PMDInputDataTableToolbar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { StatisticsModeDIR } from '../../../../utils/graphs/types';
import DataTableDIRSkeleton from './DataTableDIRSkeleton';
import DIRInputDataTableToolbar from '../../../Common/DataTable/Toolbar/DIRInputDataTableToolbar';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';

import { useTheme } from '@mui/material/styles';
import { primaryColor } from '../../../../utils/ThemeConstants';
import { DIRDataTableColumns, IDataTableDIR } from '../types';
import reverseDirectionsByIds from '../../../../utils/files/transforms/reverseDirectionsByIds';
import centerDirectionsByMean from '../../../../utils/files/transforms/centerDirectionsByMean';
import meanDirectionsOf from '../../../../utils/files/transforms/meanDirectionsOf';

const DataTableDIR: FC<IDataTableDIR> = ({ data }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    selectedDirectionsIDs,
    hiddenDirectionsIDs,
    reversedDirectionsIDs,
    centeredByMean,
    currentInterpretation,
  } = useAppSelector((state) => state.dirPageReducer);

  // selectionModel is array of ID's of rows
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedRows, setSelectedRows] = useState<Array<DataGridDIRFromDIRRow>>([]);

  useEffect(() => {
    if (selectedDirectionsIDs) setSelectionModel(selectedDirectionsIDs);
    else setSelectionModel([]);
  }, [selectedDirectionsIDs]);

  const toggleRowVisibility = (id: number) => (event: any) => {
    event.stopPropagation();
    const newhiddenDirectionsIDs = hiddenDirectionsIDs.includes(id)
      ? hiddenDirectionsIDs.filter((hiddenId) => hiddenId !== id)
      : [...hiddenDirectionsIDs, id];
    dispatch(setHiddenDirectionsIDs(newhiddenDirectionsIDs));
  };

  const toggleAllRowsVisibility = (event: any) => {
    dispatch(setHiddenDirectionsIDs([]));
  };

  const toggleRowPolarity = (id: number) => (event: any) => {
    event.stopPropagation();
    const newReversedDirectionsIDs = reversedDirectionsIDs.includes(id)
      ? reversedDirectionsIDs.filter((reversedId) => reversedId !== id)
      : [...reversedDirectionsIDs, id];
    dispatch(setReversedDirectionsIDs(newReversedDirectionsIDs));
  };

  const toggleAllRowsPolarity = (event: any) => {
    reversedDirectionsIDs.length > 0
      ? dispatch(setReversedDirectionsIDs([]))
      : dispatch(
          setReversedDirectionsIDs(
            data?.interpretations?.map((interpretation) => interpretation.id) ?? [],
          ),
        );
  };

  const columns: DIRDataTableColumns = [
    {
      field: 'toggleVisibility',
      type: 'actions',
      width: 40,
      renderHeader: () => (
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="Hide all directions"
          onClick={toggleAllRowsVisibility}
          color="inherit"
        />
      ),
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={
              hiddenDirectionsIDs.includes(id as number) ? (
                <VisibilityOffIcon />
              ) : (
                <VisibilityIcon />
              )
            }
            label="Toggle direction visibility"
            onClick={toggleRowVisibility(id as number)}
            color="inherit"
          />,
        ];
      },
    },
    {
      field: 'reversePolarity',
      type: 'actions',
      width: 40,
      renderHeader: () => (
        <GridActionsCellItem
          icon={<SwapVertRoundedIcon />}
          label="Reverse polarity for all directions"
          onClick={toggleAllRowsPolarity}
          color="inherit"
        />
      ),
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={
              reversedDirectionsIDs.includes(id as number) ? (
                <SettingsBackupRestoreIcon sx={{ fill: primaryColor(theme.palette.mode) }} />
              ) : (
                <SwapVertRoundedIcon />
              )
            }
            label="Toggle direction polarity"
            onClick={toggleRowPolarity(id as number)}
            color="inherit"
          />,
        ];
      },
    },
    { field: 'id', headerName: 'ID', type: 'number', width: 40 },
    { field: 'index', headerName: '№', type: 'number', width: 40 },
    { field: 'label', headerName: 'Label', type: 'string', width: 90 },
    { field: 'code', headerName: 'Code', type: 'string', width: 80 },
    { field: 'stepRange', headerName: 'StepRange', type: 'string', width: 120 },
    { field: 'stepCount', headerName: 'N', type: 'number', width: 40 },
    {
      field: 'Dgeo',
      headerName: 'Dgeo',
      type: 'number',
      width: 70,
      valueFormatter: (params: GridValueFormatterParams) => (params.value as number)?.toFixed(1),
    },
    {
      field: 'Igeo',
      headerName: 'Igeo',
      type: 'number',
      width: 70,
      valueFormatter: (params: GridValueFormatterParams) => (params.value as number)?.toFixed(1),
    },
    {
      field: 'accuracyGeo',
      headerName: 'Kgeo',
      type: 'string',
      width: 70,
      valueFormatter: (params: GridValueFormatterParams) => (params.value as number)?.toFixed(1),
    },
    {
      field: 'confidenceRadiusGeo',
      headerName: 'MADgeo',
      type: 'string',
      width: 80,
      valueFormatter: (params: GridValueFormatterParams) => (params.value as number)?.toFixed(1),
    },
    {
      field: 'Dstrat',
      headerName: 'Dstrat',
      type: 'number',
      width: 70,
      valueFormatter: (params: GridValueFormatterParams) => (params.value as number)?.toFixed(1),
    },
    {
      field: 'Istrat',
      headerName: 'Istrat',
      type: 'number',
      width: 70,
      valueFormatter: (params: GridValueFormatterParams) => (params.value as number)?.toFixed(1),
    },
    {
      field: 'accuracyStrat',
      headerName: 'Kstrat',
      type: 'string',
      width: 70,
      valueFormatter: (params: GridValueFormatterParams) => (params.value as number)?.toFixed(1),
    },
    {
      field: 'confidenceRadiusStrat',
      headerName: 'MADstrat',
      type: 'string',
      width: 80,
      valueFormatter: (params: GridValueFormatterParams) => (params.value as number)?.toFixed(1),
    },
    { field: 'comment', headerName: 'Comment', type: 'string', width: 200 },
    { field: 'lat', headerName: 'Lat', type: 'number', width: 70 },
    { field: 'lon', headerName: 'Lon', type: 'number', width: 70 },
  ];

  columns.forEach((col) => {
    col.align = 'center';
    col.headerAlign = 'center';
    col.hideSortIcons = true;
  });

  // Apply the same reversal + center-by-mean the stereonet uses, so the table
  // matches the graph (and the centered export). Centering needs a Fisher mean.
  // Memoised so the per-direction rotations don't rerun on every unrelated
  // render (row selection, hover, theme, …).
  const means = useMemo(() => meanDirectionsOf(currentInterpretation), [currentInterpretation]);
  const displayData = useMemo(() => {
    if (!data) return null;
    const reversed = reverseDirectionsByIds(data, reversedDirectionsIDs);
    if (centeredByMean && means) {
      return centerDirectionsByMean(reversed, means.geographic, means.stratigraphic);
    }
    return reversed;
  }, [data, reversedDirectionsIDs, centeredByMean, means]);

  let visibleIndex = 1;
  const rows: Array<DataGridDIRFromDIRRow> = displayData
    ? displayData.interpretations.map((interpretation) => {
        const {
          id,
          label,
          code,
          stepRange,
          stepCount,
          Dgeo,
          Igeo,
          Dstrat,
          Istrat,
          MADgeo,
          Kgeo,
          MADstrat,
          Kstrat,
          comment,
        } = interpretation;
        return {
          id,
          index: hiddenDirectionsIDs.includes(id) ? '-' : visibleIndex++,
          label,
          code: code as StatisticsModeDIR,
          stepRange,
          stepCount,
          Dgeo: +Dgeo.toFixed(1),
          Igeo: +Igeo.toFixed(1),
          Dstrat: +Dstrat.toFixed(1),
          Istrat: +Istrat.toFixed(1),
          confidenceRadiusGeo: +MADgeo.toFixed(1),
          accuracyGeo: +(Kgeo || 0).toFixed(1),
          confidenceRadiusStrat: +MADstrat.toFixed(1),
          accuracyStrat: +(Kstrat || 0).toFixed(1),
          comment,
        };
      })
    : [];

  return (
    <DataTablePMDSkeleton>
      {data && (
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(e) => {
            setSelectionModel(e);
            const selectedIDs = new Set(e);
            if ([...selectedIDs].length > 0) dispatch(setSelectedDirectionsIDs([...selectedIDs]));
            else dispatch(setSelectedDirectionsIDs(null));
            const selectedRows = rows.filter((r) => selectedIDs.has(r.id));
            setSelectedRows(selectedRows);
          }}
          components={{
            Toolbar: DIRInputDataTableToolbar,
          }}
          sx={{
            ...getDataTableBaseStyle(theme.palette.mode),
            '& .MuiDataGrid-cell': {
              padding: '0px 0px',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: '0px 0px',
            },
          }}
          density={'compact'}
          hideFooter={rows.length < 100}
          disableVirtualization={rows.length < 100}
          getRowClassName={(params) =>
            hiddenDirectionsIDs.includes(params.row.id) ? styles.hiddenRow : ''
          }
        />
      )}
    </DataTablePMDSkeleton>
  );
};

export default DataTableDIR;
